// Video for one camera: asks /api/video/session for a StreamDescriptor (live, or archive at
// a given time) and renders it by kind: sim → canvas, media → <video>, mjpeg → <img>, webrtc → notice.
// VideoPlayer is the Phase 0 single-camera player (alarm list drawer); CameraFeed is the bare feed
// the operator console arranges in its single / quad grid and drives with its own controls.
// Both report watch time for their session through useWatchTracker (hooks/useWatchTracker.ts).

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { api, type StreamDescriptor } from '../api.ts';
import { useLoad } from '../hooks.ts';
import { useWatchTracker } from '../hooks/useWatchTracker.ts';
import { fmtDateTime, fmtTime, OPERATOR_TZ } from '../time.ts';
import { Icon } from './Icon.tsx';
import { ContinuousPlayer, followStep } from './continuous.ts';
import { SimFeed, type ModeBadge } from './SimFeed.tsx';
import { ErrorBox } from './ui.tsx';

const PRE_ROLL_MS = 10_000;

/* ---------- simulator detection (used by the camera rail for still frames) ---------- */

const simSeeds = new Map<string, string>();
let simSeen = false;
const simSubs = new Set<() => void>();
function noteDescriptor(d: StreamDescriptor): void {
  if (d.kind !== 'sim') return;
  simSeeds.set(d.camera.id, d.sim?.seed ?? d.camera.id);
  if (!simSeen) {
    simSeen = true;
    for (const cb of simSubs) cb();
  }
}
/** True once any video session came back as `sim` (no NX connected). */
export function useSimMode(): boolean {
  return useSyncExternalStore(
    (cb) => {
      simSubs.add(cb);
      return () => simSubs.delete(cb);
    },
    () => simSeen,
    () => false,
  );
}
export function simSeedFor(cameraId: string): string {
  return simSeeds.get(cameraId) ?? cameraId;
}

/* ---------- renderers ---------- */

function Overlay({ d, badge, behindS }: { d: StreamDescriptor; badge?: ModeBadge; behindS?: number | null }) {
  const b = badge ?? { text: d.mode === 'live' ? 'LIVE' : 'ARCHIVE', cls: d.mode };
  return (
    <div className="osd tl">
      <span className={`mode ${b.cls}`}>{b.text}</span>
      <span className="cn">{d.camera.name}</span>
      {behindS != null && (
        <span className="cn dim" title="Playing on from the recording into live, nothing skipped. Live (L) jumps to now.">
          {behindS} s behind live
        </span>
      )}
    </div>
  );
}

type OnPlaying = ((playing: boolean) => void) | undefined;
/** A recorded stream reached its end; the argument is the recording time it ended at (epoch ms). */
type OnEnded = ((endedAt: number) => void) | undefined;
/** The timeline's playhead (epoch ms) a quad tile keeps to; null while live. */
type Playhead = (() => number | null) | undefined;

/** Tells the watch tracker whether this renderer is playing; reports false when it goes away. */
function usePlayingReport(onPlaying: OnPlaying, playing: boolean): void {
  useEffect(() => {
    onPlaying?.(playing);
  }, [onPlaying, playing]);
  useEffect(() => () => onPlaying?.(false), [onPlaying]);
}

/** Why a stream failed, from the server's answer: a <video> element only reports an error code. */
async function streamProblem(url: string, code: number | undefined): Promise<string> {
  const ac = new AbortController();
  try {
    const r = await fetch(url, { signal: ac.signal });
    if (!r.ok) {
      const body = (await r.json().catch(() => null)) as { error?: string } | null;
      return body?.error ? `${body.error.charAt(0).toUpperCase()}${body.error.slice(1)}.` : `The video server answered ${r.status}.`;
    }
  } catch {
    // unreachable: fall through to the element's own error
  } finally {
    ac.abort(); // a stream that does start is not watched twice
  }
  return code === MediaError.MEDIA_ERR_DECODE
    ? 'This browser could not decode the video.'
    : code === MediaError.MEDIA_ERR_NETWORK
      ? 'The video stream was interrupted.'
      : 'The video could not be played.';
}

/**
 * NX video through core. Recorded video plays continuously through Media Source (continuous.ts): the session's stream,
 * then the recording after it, then live, as one, so nothing after the alarm is skipped. Live video, and recorded video
 * this browser can't play that way (WebM when NX transcodes an H.265 camera, no Media Source), is a plain <video> on
 * the session's stream URL, which plays as bytes arrive. Seeking opens a new session at the new time.
 */
function MediaVideo({ d, paused, badge, controls, onPlaying, onEnded, tile = false, playhead }: { d: StreamDescriptor; paused: boolean; badge?: ModeBadge; controls: boolean; onPlaying: OnPlaying; onEnded?: OnEnded; tile?: boolean; playhead?: Playhead }) {
  const ref = useRef<HTMLVideoElement | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  usePlayingReport(onPlaying, playing && !err);
  const playheadRef = useRef(playhead);
  playheadRef.current = playhead;
  const url = d.url;
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;
  const atMs = d.mode === 'archive' && d.at ? Date.parse(d.at) : NaN;
  const [plain, setPlain] = useState(false);
  const continuous = !plain && !!url && !Number.isNaN(atMs) && !!d.viewId && ContinuousPlayer.supported();
  const player = useRef<ContinuousPlayer | null>(null);
  const [following, setFollowing] = useState(false);
  const [behindS, setBehindS] = useState<number | null>(null);
  useEffect(() => {
    setBehindS(null);
    if (!following) return;
    const id = setInterval(() => {
      const v = ref.current;
      const p = player.current;
      if (v && p) setBehindS(Math.max(0, Math.round((Date.now() - p.wallAt(v.currentTime)) / 1000)));
    }, 1000);
    return () => clearInterval(id);
  }, [following]);

  // Watch time counts only while the element is really playing (not paused, buffering or ended).
  useEffect(() => {
    const v = ref.current;
    if (!v || !url) return;
    let gone = false;
    const on = () => setPlaying(true);
    const off = () => setPlaying(false);
    const failed = () => {
      setPlaying(false);
      // The continuous player's video failed to decode before it got going: the plain <video> on the same URL instead.
      if (player.current && v.currentTime < 1) return setPlain(true);
      void streamProblem(url, v.error?.code).then((m) => !gone && setErr(m));
    };
    // A recorded stream ran out (the plain <video>: NX ends it at what was recorded when it was opened; the continuous
    // player only after NX failed it repeatedly): report where, so the console carries on.
    const ended = () => {
      const p = player.current;
      if (p) onEndedRef.current?.(p.wallAt(v.currentTime));
      else if (!Number.isNaN(atMs)) onEndedRef.current?.(atMs + v.currentTime * 1000);
    };
    const offEvents = ['pause', 'waiting', 'ended', 'emptied'] as const;
    v.addEventListener('playing', on);
    v.addEventListener('error', failed);
    v.addEventListener('ended', ended);
    for (const n of offEvents) v.addEventListener(n, off);
    return () => {
      gone = true;
      v.removeEventListener('playing', on);
      v.removeEventListener('error', failed);
      v.removeEventListener('ended', ended);
      for (const n of offEvents) v.removeEventListener(n, off);
    };
  }, [url, atMs]);

  useEffect(() => {
    const video = ref.current;
    if (!video || !url) return;
    setErr(null);
    if (continuous) {
      const viewId = d.viewId!;
      let base = url;
      const p = new ContinuousPlayer(
        video,
        atMs,
        {
          first: url,
          follow: (q) => `${base}?${'live' in q ? 'live=1' : `from=${q.from}`}`,
          renew: async () => {
            base = (await api.videoTicket(viewId, tile)).url;
          },
        },
        { unsupported: () => setPlain(true), failed: setErr, following: setFollowing },
      );
      player.current = p;
      p.start();
      return () => {
        player.current = null;
        setFollowing(false);
        p.stop();
      };
    }
    video.src = url;
    video.play().catch(() => undefined);
    return () => {
      // Ends the request, which ends the stream from NX.
      video.removeAttribute('src');
      video.load();
    };
  }, [url, continuous]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (paused) v.pause();
    else v.play().catch(() => undefined);
  }, [paused]);

  // A quad tile keeps to the timeline's playhead (followStep), so the tiles show the same moment as the main camera
  // and the timeline even after one of them stalled. Nothing to keep to while live.
  useEffect(() => {
    if (!continuous) return;
    const id = setInterval(() => {
      const v = ref.current;
      const p = player.current;
      const at = playheadRef.current?.();
      if (!v || !p || at == null || v.paused || v.readyState < 3) return;
      const step = followStep((p.wallAt(v.currentTime) - at) / 1000, v.currentTime, (t) =>
        Array.from({ length: v.buffered.length }, (_, i) => i).some((i) => t >= v.buffered.start(i) && t < v.buffered.end(i) - 0.5),
      );
      if (step.seekTo !== undefined) v.currentTime = step.seekTo;
      if (v.playbackRate !== step.rate) v.playbackRate = step.rate;
    }, 1000);
    return () => clearInterval(id);
  }, [url, continuous]);

  if (!url) return <div className="feed off"><span className="nosig">Stream has no URL</span></div>;
  return (
    <div className="feed">
      <video ref={ref} muted playsInline autoPlay preload="auto" controls={controls} aria-label={`${d.mode} video of ${d.camera.name}`} />
      <Overlay d={d} badge={badge} behindS={behindS} />
      {err && <div className="feed-err" role="alert">{err}</div>}
    </div>
  );
}

function MjpegView({ d, badge, onPlaying }: { d: StreamDescriptor; badge?: ModeBadge; onPlaying: OnPlaying }) {
  const [err, setErr] = useState(false);
  // MJPEG has no pause: it plays while the stream loads without error.
  usePlayingReport(onPlaying, !!d.url && !err);
  if (!d.url) return <div className="feed off"><span className="nosig">Stream has no URL</span></div>;
  return (
    <div className="feed">
      {err ? (
        <div className="feed off"><span className="nosig">MJPEG stream failed to load</span></div>
      ) : (
        <img src={d.url} alt={`${d.mode} MJPEG view of ${d.camera.name}`} onError={() => setErr(true)} />
      )}
      <Overlay d={d} badge={badge} />
    </div>
  );
}

/** The simulated feed counts as playing whenever it is not paused. */
function SimVideo({ d, paused, badge, onPlaying }: { d: StreamDescriptor; paused: boolean; badge?: ModeBadge; onPlaying: OnPlaying }) {
  usePlayingReport(onPlaying, !paused);
  const startAt = d.mode === 'archive' && d.at ? Date.parse(d.at) : Date.now();
  return (
    <SimFeed
      seed={d.sim?.seed ?? d.camera.id}
      label={d.sim?.label ?? ''}
      mode={d.mode}
      startAt={Number.isNaN(startAt) ? Date.now() : startAt}
      timeZone={d.camera.siteTimeZone}
      cameraName={d.camera.name}
      paused={paused}
      badge={badge}
    />
  );
}

function Feed({ d, paused = false, badge, controls = true, onPlaying, onEnded, tile, playhead }: { d: StreamDescriptor; paused?: boolean; badge?: ModeBadge; controls?: boolean; onPlaying?: OnPlaying; onEnded?: OnEnded; tile?: boolean; playhead?: Playhead }) {
  switch (d.kind) {
    case 'sim':
      return <SimVideo d={d} paused={paused} badge={badge} onPlaying={onPlaying} />;
    case 'media':
      return <MediaVideo d={d} paused={paused} badge={badge} controls={controls} onPlaying={onPlaying} onEnded={onEnded} tile={tile} playhead={playhead} />;
    case 'mjpeg':
      return <MjpegView d={d} badge={badge} onPlaying={onPlaying} />;
    case 'webrtc':
      return (
        <div className="feed off">
          <span className="nosig info">WebRTC arrives with spike S1</span>
          <Overlay d={d} badge={badge} />
        </div>
      );
  }
}

/* ---------- console feed ---------- */

/** Events whose first frame this page already reported (the server stamps once anyway; this saves the requests). */
const firstFrameSent = new Set<string>();

/**
 * Reports the first frame shown on an event's video once it is on screen (not pre-staged and playing): the
 * accept → first video frame segment (architecture §5.1). The server ignores it unless this operator owns the event and
 * accepted it in the last 10 minutes, so opening video on someone else's event or much later records nothing.
 */
function useFirstFrameReport(eventId: string | null | undefined, cameraId: string, prestage: boolean): (playing: boolean) => void {
  const [playing, setPlaying] = useState(false);
  const wasPrestaged = useRef(prestage);
  if (prestage) wasPrestaged.current = true;
  useEffect(() => {
    if (!eventId || prestage || !playing || firstFrameSent.has(eventId)) return;
    firstFrameSent.add(eventId);
    api.firstFrame(eventId, cameraId, wasPrestaged.current).then(
      (r) => {
        // Not stamped (not accepted yet, or not ours): allow a later report, after an accept on this page.
        if (!r.stamped) firstFrameSent.delete(eventId);
      },
      () => firstFrameSent.delete(eventId),
    );
  }, [eventId, cameraId, prestage, playing]);
  return setPlaying;
}

export interface CameraFeedProps {
  cameraId: string;
  cameraName: string;
  /** ISO time for an archive session; null for live. */
  at: string | null;
  /** Records a video_review action on this event (Phase 1). */
  eventId?: string | null;
  /** The player shows the alarm clip (sent as ?mode=alarm for watch time). */
  alarmClip?: boolean;
  paused?: boolean;
  badge?: ModeBadge;
  offline?: boolean;
  /** Bumped to request a fresh session for the same camera and time. */
  nonce?: number;
  /**
   * The watch-time view ended (page hidden, or the server dropped it) and a fresh session is needed.
   * Default: request a new session for the same camera and `at`.
   */
  onRenew?: () => void;
  /**
   * Pre-staged while an offer is showing (api-phase2.md): the session is marked prestage and the player is
   * rendered off screen, so none of its time counts as watched. It ends as `closed` when it unmounts.
   */
  prestage?: boolean;
  /** A recorded stream reached its end (at this recording time): the console carries on from there. */
  onEnded?: (endedAt: number) => void;
  /**
   * A quad tile that isn't the main camera: NX's secondary (low-resolution) stream, so a quad holds one full-size
   * stream, not four (staging, 2026-10-28). It goes on into live like the main camera.
   */
  tile?: boolean;
  /** The timeline's playhead (epoch ms; null while live), which a quad tile keeps to while it plays recording. */
  playhead?: () => number | null;
  /**
   * The view's position epoch (View.epoch). With it, a new session opens when the camera, mode or epoch changes, not
   * when only `at` moves because the view was restated from the playhead; the feed plays on. Without it, `at` decides.
   */
  epoch?: number;
}

/** One camera's video, filling its (position: relative) parent. */
export function CameraFeed({ cameraId, cameraName, at, eventId, alarmClip = false, paused = false, badge, offline, nonce = 0, onRenew, prestage = false, onEnded, tile = false, playhead, epoch }: CameraFeedProps) {
  const [retry, setRetry] = useState(0);
  const mode = alarmClip && at ? 'alarm' : null;
  // The position a session opens at is the latest `at`; what reopens it is the epoch (when given).
  const atRef = useRef(at);
  atRef.current = at;
  const live = at === null;
  const session = useLoad(
    (signal) =>
      api.videoSession(cameraId, atRef.current, signal, eventId, mode, prestage, tile).then((d) => {
        noteDescriptor(d);
        return d;
      }),
    [cameraId, epoch === undefined ? at : `${epoch}|${live}`, eventId, mode, nonce, retry, prestage, tile],
  );
  const d = session.data;
  const watch = useWatchTracker(session.error ? null : d?.viewId, onRenew ?? (() => setRetry((n) => n + 1)), !prestage);
  const frame = useFirstFrameReport(eventId, cameraId, prestage);
  const onPlaying = useCallback(
    (p: boolean) => {
      watch.setPlaying(p);
      frame(p);
    },
    [watch.setPlaying, frame],
  );
  if (session.error) {
    return (
      <div className="feed off">
        <div className="feed-errbox">
          <ErrorBox error={session.error} title={`Could not open video for ${cameraName}`} onRetry={() => setRetry((n) => n + 1)} />
        </div>
      </div>
    );
  }
  if (!d) {
    return (
      <div className="feed off">
        <div className="osd tl">
          <span className="cn">{cameraName}</span>
        </div>
        <span className="nosig info">{offline ? 'Camera reported offline · opening anyway…' : 'Opening video session…'}</span>
      </div>
    );
  }
  return <Feed key={`${d.kind}|${d.mode}|${d.at ?? 'now'}|${d.url ?? ''}`} d={d} paused={paused} badge={badge} controls={false} onPlaying={onPlaying} onEnded={onEnded} tile={tile} playhead={playhead} />;
}

/* ---------- Phase 0 player ---------- */

export interface VideoPlayerProps {
  cameraId: string;
  /** occurredAt of the alarm; enables "At alarm time". */
  alarmAt?: string | null;
  initial?: 'live' | 'alarm';
}

export function VideoPlayer({ cameraId, alarmAt, initial }: VideoPlayerProps) {
  const canArchive = !!alarmAt && !Number.isNaN(Date.parse(alarmAt));
  const [target, setTarget] = useState<'live' | 'alarm'>(initial ?? (canArchive ? 'alarm' : 'live'));
  const [nonce, setNonce] = useState(0);
  const at = target === 'alarm' && canArchive && alarmAt ? new Date(Date.parse(alarmAt) - PRE_ROLL_MS).toISOString() : null;
  const session = useLoad((signal) => api.videoSession(cameraId, at, signal, null, at ? 'alarm' : null), [cameraId, at, nonce]);
  const d = session.data;
  const watch = useWatchTracker(session.error ? null : d?.viewId, () => setNonce((n) => n + 1));

  return (
    <div className="player">
      <div className="pctl">
        <div className="seg" role="radiogroup" aria-label="Video source">
          <button type="button" role="radio" aria-checked={target === 'live'} className={target === 'live' ? 'on' : ''} onClick={() => setTarget('live')}>
            <Icon name="play" />
            Live
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={target === 'alarm'}
            className={target === 'alarm' ? 'on' : ''}
            disabled={!canArchive}
            title={canArchive ? 'Archive from 10 s before the alarm' : 'No alarm time to seek to'}
            onClick={() => setTarget('alarm')}
          >
            <Icon name="log" />
            At alarm time
          </button>
        </div>
        <button type="button" className="btn icon" onClick={() => setNonce((n) => n + 1)} title="Request a fresh stream session" aria-label="Reload stream">
          <Icon name="sync" />
        </button>
        <span className="sp" />
        {d && (
          <span className="note mono" title={d.expiresAt ? `Session expires ${fmtDateTime(d.expiresAt, OPERATOR_TZ)}` : undefined}>
            {d.kind.toUpperCase()} · {d.mode}
            {d.at ? ` · from ${fmtDateTime(d.at, d.camera.siteTimeZone)}` : ''}
            {d.expiresAt ? ` · expires ${fmtTime(d.expiresAt, OPERATOR_TZ)}` : ''}
          </span>
        )}
      </div>
      <div className="stage16">
        {session.error ? (
          <div className="feed off">
            <div className="feed-errbox">
              <ErrorBox error={session.error} title="Could not open the video session" onRetry={() => setNonce((n) => n + 1)} />
            </div>
          </div>
        ) : !d ? (
          <div className="feed off">
            <span className="nosig info">Opening video session…</span>
          </div>
        ) : (
          <Feed key={`${d.kind}|${d.mode}|${d.at ?? 'now'}|${d.url ?? ''}|${nonce}`} d={d} onPlaying={watch.setPlaying} onEnded={() => setTarget('live')} />
        )}
      </div>
    </div>
  );
}
